import {
    ChangeEvent,
    FormEvent,
    useState
} from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import {
    Box,
    Button,
    Card,
    Container,
    FormControl,
    FormLabel,
    Input,
    Text,
    Textarea
} from '@chakra-ui/react'
import { EmailIcon } from '@chakra-ui/icons'

import { useTranslation } from "react-i18next";

import { Config } from "../../config/Config";
import { IContactRequest } from "../../models/IContactRequest";

// Map the backend ProblemDetails `detail` (or `title`) to a specific, user-facing message.
const friendlyContactError = (detail: string, status: number): string => {
    const d = detail.toLowerCase();
    if (d.includes("invalid message content")) {
        return "Your message looks too short or unusual. Please write at least a full sentence (about 30 characters), then try again.";
    }
    if (d.includes("captcha")) {
        return "We couldn't verify your submission. Please reload the page and try again.";
    }
    if (d.includes("bot detected")) {
        return "Your submission was flagged as automated. Please wait a few seconds and try again.";
    }
    if (d.includes("not a valid email")) {
        return "Please enter a valid email address.";
    }
    if (d.includes("required")) {
        return "Please enter your email address before submitting.";
    }
    if (status === 429) {
        return "Too many attempts. Please wait a minute and try again.";
    }
    return "Something went wrong. Please try again later.";
};

export const ContactUsDetails = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const { t } = useTranslation();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSending, setIsSending] = useState(false);
	const [ isErrorVisible, setIsErrorVisible ] = useState(false);
	const [ errorMessage, setErrorMessage ] = useState("");

    const [formData, setFormData] = useState<IContactRequest>({
        email: '',
        phoneNumber: '',
        message: '',
		website: '',
		recaptchaToken: '',
		timestamp: Date.now(),
    });

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    	setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
		setIsErrorVisible(false);
        
        if (!executeRecaptcha) {
            console.error('reCAPTCHA is not ready');
			setErrorMessage("reCAPTCHA is not ready, please try again in a few seconds.");
			setIsErrorVisible(true);
            return;
        }
        
        setIsSending(true);
        try {
            const token = await executeRecaptcha('contact_us');
            const dataToSend = { ...formData, recaptchaToken: token };
            const response = await fetch(`${Config.baseUrl}api/v1/contactrequest`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                // The API returns a ProblemDetails body ({ title, detail, status }).
                // Surface a specific, friendly reason instead of a generic failure.
                let detail = "";
                try {
                    const problem = await response.json();
                    detail = (problem?.detail || problem?.title || "").toString();
                } catch {
                    // body wasn't JSON; fall back to the generic message below
                }
                setErrorMessage(friendlyContactError(detail, response.status));
                setIsErrorVisible(true);
                return;
            }

            console.log(await response.json());
            setIsSubmitted(true);
        } catch (error) {
            console.error('Contact form submission error:', error);
			setErrorMessage("Something went wrong. Please try again later.");
			setIsErrorVisible(true);
        } finally {
            setIsSending(false);
        }
    };


    return (
		<Container mt="-1em" py={{ base: "16", md: "12" }}>
			<Card>
				<Box
					bg="bg.surface"
					boxShadow="sm"
					borderRadius="lg"
					p={{ base: "4", md: "6" }}
				>
                {isSubmitted ? (
						<Text>{t('contactUs.submissionSuccess')}</Text>
                    ) : (                    
                    <form onSubmit={handleSubmit}>

                        <FormControl isRequired>
                            <FormLabel>{t('contactUs.formLabelEmail')}</FormLabel>
                            <Input
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </FormControl>

                        <FormControl mt={6}>
                            <FormLabel>{t('contactUs.formLabelPhone')}</FormLabel>
                            <Input
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                            />
                        </FormControl>

                        <FormControl mt={6}>
                            <FormLabel>{t('contactUs.formLabelMessage')}</FormLabel>
                            <Textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                            />
                        </FormControl>

                        <Text mt={6}>
                            {t('contactUs.neverShare')}
                        </Text>

                        <Button 
                            leftIcon={<EmailIcon />} 
                            variant="primary" 
                            mt={4} 
                            type="submit" 
                            isLoading={isSending} 
                            loadingText={t('contactUs.formButtonSending')}
                        >
                            {t('contactUs.formButtonSubmit')}
                        </Button>

						{
							isErrorVisible && (
								<Text mt={4} color="red.500">
									{errorMessage}
								</Text>
							)
						}
                        
                    </form>
                )}
                </Box>
            </Card>
        </Container>
    );
  }
